import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useCapabilities } from '../../Context/CapabilityContext';

// ============================================
// ANIMATIONS
// ============================================

const slideUpward = keyframes`
  0% { transform: translateY(0px); }
  3.33% { transform: translateY(-50px); }
  6.66% { transform: translateY(-100px); }
  10% { transform: translateY(-150px); }
  13.33% { transform: translateY(-200px); }
  16.66% { transform: translateY(-250px); }
  20% { transform: translateY(-300px); }
  23.33% { transform: translateY(-350px); }
  26.66% { transform: translateY(-400px); }
  30% { transform: translateY(-450px); }
  33.33% { transform: translateY(-500px); }
  36.66% { transform: translateY(-550px); }
  40% { transform: translateY(-600px); }
  43.33% { transform: translateY(-650px); }
  46.66% { transform: translateY(-700px); }
  50% { transform: translateY(-750px); }
  53.33% { transform: translateY(-800px); }
  56.66% { transform: translateY(-850px); }
  60% { transform: translateY(-900px); }
  63.33% { transform: translateY(-950px); }
  66.66% { transform: translateY(-1000px); }
  70% { transform: translateY(-1050px); }
  73.33% { transform: translateY(-1100px); }
  76.66% { transform: translateY(-1150px); }
  80% { transform: translateY(-1200px); }
  83.33% { transform: translateY(-1250px); }
  86.66% { transform: translateY(-1300px); }
  90% { transform: translateY(-1350px); }
  93.33% { transform: translateY(-1400px); }
  96.66% { transform: translateY(-1450px); }
  100% { transform: translateY(-1500px); }
`;

const fillCircle = keyframes`
  0% { opacity: 0; }
  10% { opacity: 1; }
  100% { opacity: 1; }
`;

const fillCheck = keyframes`
  0% { fill: rgba(255, 255, 255, 0.3); }
  10% { fill: #ec4899; }
  100% { fill: #ec4899; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const LoadingContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  color: #e8eaed;
  padding: 40px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Icon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: rgba(99, 102, 241, 0.15);
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 40px;
    height: 40px;
    color: #6366f1;
  }
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  max-width: 500px;
  line-height: 1.5;
`;

const PhrasesContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 200px;
  overflow: hidden;
  width: 500px;
  position: relative;
  margin-bottom: 40px;
`;

const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
`;

const PhrasesGroup = styled.g`
  animation: ${slideUpward} 45s linear infinite;
`;

const PhraseText = styled.text`
  fill: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  letter-spacing: 0.3px;
`;

const CheckGroup = styled.g``;

const CheckCircle = styled.circle`
  fill: rgba(236, 72, 153, 0.2);
  opacity: 0;
  animation: ${fillCircle} 5s forwards;
  animation-delay: ${props => props.delay}s;
`;

const CheckMark = styled.polygon`
  fill: rgba(255, 255, 255, 0.3);
  animation: ${fillCheck} 5s forwards;
  animation-delay: ${props => props.delay}s;
`;

const CircleOutline = styled.path`
  fill: rgba(255, 255, 255, 0.2);
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: auto;
`;

const FooterText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.3px;
`;

const ProgressBar = styled.div`
  width: 400px;
  height: 6px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 8px;
  width: ${props => props.progress}%;
  transition: width 0.4s ease;
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.4);
`;

// ============================================
// COMPONENT
// ============================================

/**
 * Steps shown while the analysis runs. This screen renders on every analysis, for every
 * discipline — so a financial adviser was watching "Checking visa eligibility" scroll past.
 *
 * The neutral list works for any practice; the immigration list is appended only for
 * immigration advisers, so their experience is unchanged (same phrases, still ~30 steps).
 */
const NEUTRAL_PHRASES = [
  "Reading conversation history",
  "Extracting client information",
  "Identifying case type and goals",
  "Reviewing educational background",
  "Assessing work experience",
  "Analyzing family composition",
  "Checking financial documentation",
  "Identifying potential blockers",
  "Assessing timeline feasibility",
  "Evaluating case readiness",
  "Checking documentation gaps",
  "Evaluating alternative approaches",
  "Assessing risk factors",
  "Reviewing compliance requirements",
  "Identifying action items",
  "Evaluating likelihood of success",
  "Generating recommendations",
  "Preparing detailed summary",
  "Organizing next steps",
  "Finalizing case assessment",
  "Building comprehensive report",
];

const IMMIGRATION_PHRASES = [
  "Analyzing immigration pathways",
  "Checking visa eligibility",
  "Evaluating language proficiency",
  "Reviewing travel history",
  "Reviewing country-specific rules",
  "Analyzing priority processing options",
  "Checking provincial requirements",
  "Calculating processing times",
  "Compiling immigration options",
];

const AnalysisLoadingScreen = ({ visible = true, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const { isImmigration, content } = useCapabilities();

  const phrases = useMemo(
    () => (isImmigration ? [...NEUTRAL_PHRASES, ...IMMIGRATION_PHRASES] : NEUTRAL_PHRASES),
    [isImmigration]
  );

  useEffect(() => {
    if (!visible) return;

    // Progress bar runs 5x slower (interval increased from 400ms to 2000ms)
    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev + Math.random() * 3;
        if (next >= 100) {
          clearInterval(interval);
          if (onComplete) {
            setTimeout(onComplete, 500);
          }
          return 100;
        }
        return next;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [visible, onComplete]);

  const createCheckmark = (yOffset, index) => {
    const delay = (index - 1.5) * 1;
    return (
      <CheckGroup key={`check-${index}`} transform={`translate(10 ${yOffset - 20}) scale(0.9)`}>
        <CheckCircle
          cx="16"
          cy="16"
          r="15"
          delay={delay}
        />
        <CheckMark
          points="21.661,7.643 13.396,19.328 9.429,15.361 7.075,17.714 13.745,24.384 24.345,9.708"
          delay={delay}
        />
        <CircleOutline
          d="M16,0C7.163,0,0,7.163,0,16s7.163,16,16,16s16-7.163,16-16S24.837,0,16,0z M16,30C8.28,30,2,23.72,2,16C2,8.28,8.28,2,16,2 c7.72,0,14,6.28,14,14C30,23.72,23.72,30,16,30z"
        />
      </CheckGroup>
    );
  };

  return (
    <LoadingContainer>
      <Header>
        <Icon>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </Icon>
        <Title>Analyzing Case</Title>
        <Subtitle>
          Processing conversation data and generating a comprehensive {content?.domainName || 'case'} assessment
        </Subtitle>
      </Header>

      <PhrasesContainer>
        <SVGContainer>
          <defs>
            <linearGradient id="maskGradient" gradientUnits="objectBoundingBox" x2="0" y2="1">
              <stop stopColor="white" stopOpacity="0" offset="0%" />
              <stop stopColor="white" stopOpacity="1" offset="25%" />
              <stop stopColor="white" stopOpacity="1" offset="75%" />
              <stop stopColor="white" stopOpacity="0" offset="100%" />
            </linearGradient>
            <mask id="phraseMask">
              <rect width="100%" height="100%" fill="url(#maskGradient)" />
            </mask>
          </defs>
          <g mask="url(#phraseMask)">
            <PhrasesGroup>
              {phrases.map((phrase, index) => {
                const yOffset = 35 + 50 * index;
                return (
                  <React.Fragment key={index}>
                    <PhraseText x="50" y={yOffset}>
                      {phrase}...
                    </PhraseText>
                    {createCheckmark(yOffset, index)}
                  </React.Fragment>
                );
              })}
            </PhrasesGroup>
          </g>
        </SVGContainer>
      </PhrasesContainer>

      <Footer>
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
        <FooterText>{Math.round(progress)}% Complete</FooterText>
      </Footer>
    </LoadingContainer>
  );
};

export default AnalysisLoadingScreen;

